/**
 * IndexedDB wrapper for storing song version history locally
 * Separate database from audio to avoid migration conflicts
 */

const DB_NAME = "LyricToolkitVersions";
const DB_VERSION = 1;
const STORE_NAME = "songVersions";

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("songId", "songId", { unique: false });
      }
    };
  });
};

/**
 * Save a version to IndexedDB
 */
export const saveVersion = async (
  songId,
  versionNumber,
  title,
  content,
  changeSummary,
) => {
  const id = `${songId}-v${versionNumber}`;
  const words = content.split(/\s+/).filter((w) => w.trim()).length;
  const lines = content.split("\n").filter((l) => l.trim()).length;

  const versionData = {
    id,
    songId: String(songId),
    versionNumber,
    title: title || null,
    content,
    wordCount: words,
    lineCount: lines,
    changeSummary: changeSummary || null,
    createdAt: new Date().toISOString(),
  };

  const db = await openDB();
  const tx = db.transaction([STORE_NAME], "readwrite");
  const store = tx.objectStore(STORE_NAME);

  return new Promise((resolve, reject) => {
    const request = store.put(versionData);
    request.onsuccess = () => resolve(versionData);
    request.onerror = () => reject(request.error);
  });
};

/**
 * List all versions for a song (metadata only, no content)
 */
export const listVersions = async (songId) => {
  const db = await openDB();
  const tx = db.transaction([STORE_NAME], "readonly");
  const store = tx.objectStore(STORE_NAME);
  const index = store.index("songId");

  return new Promise((resolve, reject) => {
    const request = index.getAll(String(songId));
    request.onsuccess = () => {
      const versions = (request.result || [])
        .map((v) => ({
          id: v.id,
          songId: v.songId,
          versionNumber: v.versionNumber,
          title: v.title,
          wordCount: v.wordCount,
          lineCount: v.lineCount,
          changeSummary: v.changeSummary,
          createdAt: v.createdAt,
        }))
        .sort((a, b) => b.versionNumber - a.versionNumber);
      resolve(versions);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get a specific version's full content
 */
export const getVersion = async (songId, versionNumber) => {
  const id = `${songId}-v${versionNumber}`;
  const db = await openDB();
  const tx = db.transaction([STORE_NAME], "readonly");
  const store = tx.objectStore(STORE_NAME);

  return new Promise((resolve, reject) => {
    const request = store.get(id);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get the latest version number for a song
 */
export const getMaxVersionNumber = async (songId) => {
  const versions = await listVersions(songId);
  if (versions.length === 0) return 0;
  return Math.max(...versions.map((v) => v.versionNumber));
};

/**
 * Count versions for a song
 */
export const countVersions = async (songId) => {
  const versions = await listVersions(songId);
  return versions.length;
};

/**
 * Delete oldest version(s) to stay within limit
 */
export const pruneVersions = async (songId, limit) => {
  const versions = await listVersions(songId);
  if (versions.length < limit) return;

  // Sort ascending (oldest first)
  const sorted = [...versions].sort(
    (a, b) => a.versionNumber - b.versionNumber,
  );
  const toDelete = sorted.slice(0, versions.length - limit + 1);

  const db = await openDB();
  const tx = db.transaction([STORE_NAME], "readwrite");
  const store = tx.objectStore(STORE_NAME);

  for (const v of toDelete) {
    store.delete(v.id);
  }

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(toDelete.length);
    tx.onerror = () => reject(tx.error);
  });
};

/**
 * Delete a specific version
 */
export const deleteVersion = async (songId, versionNumber) => {
  const id = `${songId}-v${versionNumber}`;
  const db = await openDB();
  const tx = db.transaction([STORE_NAME], "readwrite");
  const store = tx.objectStore(STORE_NAME);

  return new Promise((resolve, reject) => {
    const request = store.delete(id);
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Delete all versions for a song (used when song is deleted)
 */
export const deleteAllVersions = async (songId) => {
  const versions = await listVersions(songId);
  if (versions.length === 0) return;

  const db = await openDB();
  const tx = db.transaction([STORE_NAME], "readwrite");
  const store = tx.objectStore(STORE_NAME);

  for (const v of versions) {
    store.delete(v.id);
  }

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(versions.length);
    tx.onerror = () => reject(tx.error);
  });
};

export default {
  saveVersion,
  listVersions,
  getVersion,
  getMaxVersionNumber,
  countVersions,
  pruneVersions,
  deleteVersion,
  deleteAllVersions,
};
